import { createPredicate } from './condition';
import { BrandclubEventType, TrackingPixel } from './types';

const SCRIPT_SRC = 'https://c.amazon-adsystem.com/aat/amzn.js';
const SCRIPT_ID = 'amzn-pixel';
const PROVIDER = 'AmazonAds';

declare global {
    interface Window {
        amzn: (...args: any[]) => void;
    }
}

let debug = false;
/**
 * Tag ids already handed to `amzn('addTag', …)`. A Set, not a single
 * `initialized` boolean: an app may run more than one Amazon tag, and a global
 * flag silently dropped every tag after the first.
 */
const addedTags = new Set<string>();

export type AmazonRegion = 'NA' | 'EU' | 'FE';
const DEFAULT_REGION: AmazonRegion = 'NA';
export interface AmazonConfig {
    brandId: string;
    region: AmazonRegion;
    amazonTagId: string;
}

/**
 * The Amazon tag id. The platform normalizes every destination's primary id into
 * `pixelId` — `AdPixelController` create/update writes it server-side, and the
 * Pages surface (`PagesTrackingPixel`, `projectPublicPagesPixels`,
 * `APP_TRACKING_SCHEMA`, the studio form) carries ONLY `pixelId` for all of them
 * (Meta pixel id, Google tag id, Amazon tag id). `amazonTagId` is the legacy
 * Integrations field name and still wins when set.
 */
const tagIdOf = (pixel: TrackingPixel): string =>
    String(pixel.amazonTagId || pixel.pixelId || '').trim();

const log = (...args: any[]) => debug &&
    // eslint-disable-next-line no-console
    console.info(...['[amazonAds]'].concat(args));

/**
 * Check if Amazon Ads script is loaded
 */
const isLoaded = (): boolean =>
    typeof window !== 'undefined' && !!window.amzn;

/**
 * Install the paste-tag stub: `amzn.js` defines the real `window.amzn`
 * ASYNCHRONOUSLY after `onload` (it finishes booting via a second fetch of
 * itself), so anything checked or called at onload-time races the library and
 * loses. Amazon's official snippet solves this by queueing every call on a
 * stub the library drains once booted — same contract here. Without it,
 * `addTag`/`trackEvent` were silently dropped on every first-load page.
 */
const installStub = (): void => {
    if (typeof window === 'undefined' || window.amzn) return;
    const stub = function (this: unknown) {
        // eslint-disable-next-line prefer-rest-params
        stub.q.push([arguments, new Date().getTime()]);
    } as ((...args: any[]) => void) & { q: any[]; version: string };
    stub.q = [];
    stub.version = '0.0';
    window.amzn = stub;
};

/**
 * Load the Amazon Ads tracking script
 */
const loadScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
        if (isLoaded()) {
            log('Amazon Ads script already loaded');
            resolve();
            return;
        }

        if (typeof window === 'undefined') {
            reject(new Error('Cannot load Amazon Ads script in non-browser environment'));
            return;
        }

        installStub();

        // Check if script tag already exists
        if (document.getElementById(SCRIPT_ID)) {
            log('Amazon Ads script tag already exists');
            resolve();
            return;
        }

        log('Loading Amazon Ads script...');
        const script = document.createElement('script');
        script.src = SCRIPT_SRC;
        script.id = SCRIPT_ID;
        script.async = true;

        script.onload = () => {
            log('Amazon Ads script loaded successfully');
            resolve();
        };

        script.onerror = (error) => {
            log('Failed to load Amazon Ads script', error);
            reject(new Error('Failed to load Amazon Ads script'));
        };

        const firstScript = document.getElementsByTagName('script')[0];
        firstScript?.parentNode
            ? firstScript.parentNode.insertBefore(script, firstScript)
            : document.head.appendChild(script);
    });
};

/**
 * Initialize Amazon Ads tracking
 */
const initializeAmazonAds = async (brandId: string, pixel: TrackingPixel): Promise<string | undefined> => {
    const tagId = tagIdOf(pixel);
    // ponytail: region is process-global in amzn.js (one setRegion per page), so a
    // second tag wanting another region can't be honoured — the first one wins.
    // 'NA' is the only region the operator UI offers (AdPixelController has EU/FE
    // commented out) and its documented defaultValue; the Pages surface has no
    // region field at all. Give each tag its own region only if that changes.
    const region: AmazonRegion = (pixel.region as AmazonRegion) || DEFAULT_REGION;

    if (!tagId) {
        log('Amazon Ads config is missing a tag id (amazonTagId / pixelId)', pixel);
        return undefined;
    }

    if (addedTags.has(tagId)) {
        log('Amazon Ads tag already added', tagId);
        return tagId;
    }

    try {
        await loadScript();

        if (!window.amzn) {
            log('Amazon Ads script loaded but amzn object not available');
            return undefined;
        }

        // Set region and add tag
        if (addedTags.size === 0) {
            window.amzn('setRegion', region);
        }
        window.amzn('addTag', tagId);
        addedTags.add(tagId);

        log('Amazon Ads initialized with config', { brandId, region, amazonTagId: tagId });
        return tagId;
    } catch (error) {
        log('Failed to initialize Amazon Ads', error);
        return undefined;
    }
};

/**
 * Track an Amazon Ads event
 */
const trackAmazonEvent = (eventName: string, params?: Record<string, any>): void => {
    if (!isLoaded()) {
        log('Cannot track event - Amazon Ads not initialized');
        return;
    }

    try {
        if (params) {
            window.amzn('trackEvent', eventName, params);
        } else {
            window.amzn('trackEvent', eventName);
        }
        log(`Tracked ${eventName} event`, params);
    } catch (error) {
        log(`Failed to track ${eventName} event`, error);
    }
};

/**
 * Initialize Amazon Ads with configuration
 */
const init = async (config: AmazonConfig): Promise<void> => {
    await initializeAmazonAds(config.brandId, {
        pixelProvider: PROVIDER,
        region: config.region,
        amazonTagId: config.amazonTagId
    });
};

const amazonAds = {
    enableDebug: () => { debug = true; },

    track({
        brandId,
        pixels,
        eventName,
        eventType,
        eventPayload
    }: {
        brandId: string;
        pixels: TrackingPixel[];
        eventName: string;
        eventType: BrandclubEventType;
        eventPayload: any;
    }) {
        // Process each Amazon Ads pixel
        pixels.forEach(async (pixel) => {
            // Apply event filter if present
            if (pixel.eventFilter) {
                const predicate = createPredicate(pixel.eventFilter);
                if (!predicate(eventPayload)) {
                    log('eventFilter not matched', pixel.eventFilter, pixel.pixelId, eventName, eventPayload);
                    return;
                }
            }

            // Initialize Amazon Ads — gate on THIS pixel's tag, not a global flag.
            const tagId = await initializeAmazonAds(brandId, pixel);
            if (!tagId) return;
            // Track the appropriate events
            switch (eventType) {
                default:
                    trackAmazonEvent(eventName, eventPayload);
                    break;
            }
        });
    },

    grantConsent() {
        if (!isLoaded()) {
            log('Cannot grant consent - Amazon Ads not initialized');
            return;
        }
        // Amazon Ads consent is typically handled at the tag level
        // No specific consent API exposed in amzn.js
        log('Amazon Ads consent granted (managed at tag level)');
    },

    revokeConsent() {
        if (!isLoaded()) {
            log('Cannot revoke consent - Amazon Ads not initialized');
            return;
        }
        // Amazon Ads consent is typically handled at the tag level
        // No specific consent API exposed in amzn.js
        log('Amazon Ads consent revoked (managed at tag level)');
    },

    // For backward compatibility
    init,
    isLoaded
};

export default amazonAds;
